import { memo, useCallback, useMemo, useRef } from 'react'
import { PrimaryButton } from '../../components/Button'
import { Form } from 'react-bootstrap'
import { usePostAppointmentContext } from '../../context/PostAppointmentContext/PostAppointmentProvider'

import './styles.scss'
import Close from '@material-ui/icons/Close'
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined'

const Note: React.FC = () => {
  const {
    noteForPatient,
    selectedFiles,
    handleAttachments,
    isEditing,
    selectedNote,
    removedAttachmentIds,
    toggleAttachmentRemoval,
    handleRemoveAttachment,
    updateNoteForPatient,
    nextScreen,
  } = usePostAppointmentContext()
  const hiddenFileInput = useRef<HTMLInputElement>(null)

  const handleNext = useCallback(() => nextScreen(), [])

  const selectedFilesRemapped = useMemo(
    () => selectedFiles.map((file) => [...Array.from(file)]).flat(),
    [selectedFiles]
  )

  return (
    <div className="note d-flex align-items-center justify-content-center flex-column">
      <div className="container mt-3">
        <p className="label">Please leave a visit note for the patient:</p>
        <Form.Control
          as="textarea"
          value={noteForPatient}
          onChange={updateNoteForPatient}
          type="text"
          rows={10}
        />
        <section className="note-attachments mt-3" aria-label="Attachments">
          <div className="attachment-header">
            <p className="attachment-heading mb-0">Attachments</p>
            <button
              type="button"
              className="attachment-action"
              onClick={() => hiddenFileInput.current?.click()}
            >
              + Add attachment
            </button>
          </div>
          <p className="attachment-help">
            PDF, PNG, JPEG or GIF. Up to 10 files per save, 10 MB each.
          </p>
          <input
            type="file"
            className="file-input"
            multiple
            accept="application/pdf, image/png, image/jpeg, image/gif"
            ref={hiddenFileInput}
            onChange={handleAttachments}
            id="choose-file-input"
            aria-label="Choose attachments"
          />
          <div className="attachment-list">
            {isEditing &&
              selectedNote?.attachments?.map((attachment) => {
                const removed = removedAttachmentIds.includes(attachment.id)
                return (
                  <div key={attachment.id} className="attachment-row">
                    <InsertDriveFileOutlined
                      className="attachment-icon"
                      aria-hidden="true"
                    />
                    <div className="attachment-details">
                      <a
                        className="attachment-name"
                        href={attachment.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {attachment.fileName}
                      </a>
                      {removed && (
                        <span className="attachment-status is-removed">
                          Removed when you save
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="attachment-action"
                      onClick={() => toggleAttachmentRemoval(attachment.id)}
                    >
                      {removed ? 'Undo remove' : 'Remove'}
                    </button>
                  </div>
                )
              })}
            {selectedFilesRemapped.map((file, index) => (
              <div className="attachment-row" key={index}>
                <InsertDriveFileOutlined
                  className="attachment-icon"
                  aria-hidden="true"
                />
                <div className="attachment-details">
                  <span className="attachment-name">{file.name}</span>
                  <span className="attachment-status is-pending">
                    Pending upload
                  </span>
                </div>
                <button
                  type="button"
                  className="attachment-action attachment-remove"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => handleRemoveAttachment(index)}
                >
                  <Close fontSize="small" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          {isEditing && (
            <p className="attachment-help mt-2 mb-0">
              Removed files leave the current note and stay in the edit history.
              Changes apply when you save the edit.
            </p>
          )}
        </section>
        <div className="d-flex flex-column align-items-end mt-3">
          <PrimaryButton
            onClick={handleNext}
            disabled={!noteForPatient}
            theme="dark"
          >
            <>Next</>
          </PrimaryButton>
        </div>
      </div>
    </div>
  )
}

export default memo(Note)
